import { motion } from "framer-motion";
import { Eye, Target, Star } from "lucide-react";
import PageShell from "@/components/PageShell";
import PageHeader from "@/components/PageHeader";
import SiteFooter from "@/components/SiteFooter";
import CTASection from "@/components/sections/CTASection";
import Counter from "@/components/common/Counter";
import { pickImage } from "@/data/imagePool";
import { I } from "@/data/imageDefaults";

const STATS = [
  { v: 5000, suffix: "+", l: "Projects Completed" },
  { v: 1000, suffix: "+", l: "Happy Clients" },
  { v: 500, suffix: "+", l: "Products" },
  { v: 24, suffix: "/7", l: "Worldwide Delivery" },
];

const REVIEWS = [
  { name: "Aisha Al Mansouri", role: "Marketing Director, Falcon Holdings", quote: "Printzee delivered our regional product launch flawlessly — every detail felt deliberate and on-brand." },
  { name: "Rohan Kapoor", role: "Brand Manager, Meridian Group", quote: "The cleanest large-format printing we've used in the UAE. Colours match across every substrate and every deadline is hit." },
  { name: "Sara Hassan", role: "HR Lead, Northstar Bank", quote: "Our welcome kits and festive gifting feel personal at scale — the team genuinely looks forward to receiving them." },
  { name: "Khalid Al-Rashid", role: "Operations Manager, Atlas Retail", quote: "From design to delivery, Printzee handled our store rollout across 12 locations seamlessly. Highly recommended." },
];

export default function About() {
  const image = pickImage("corp", "Printzee team", 0);
  const src = image.includes("photos") || image.includes("unsplash") || image.includes("picsum") ? I.corp : image;

  return (
    <PageShell>
      <PageHeader
        overline="About Printzee"
        title={<>Building lasting impressions —<br />one brand at a time.</>}
        subtitle="A premium branding, printing, signage, display and event solutions partner — based in Dubai, serving the world."
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "About" }]}
      />

      {/* Story */}
      <section className="bg-white dark:bg-[#0B1120] py-20 md:py-28 transition-colors">
        <div className="pz-container grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="pz-overline">Our Story</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-[#0F172A] dark:text-white">A reliable one-stop partner for the UAE&apos;s most demanding brands.</h2>
            <div className="pz-gold-line mt-5" />
            <p className="mt-6 text-slate-600 dark:text-white/70 leading-relaxed">
              Printzee.ae provides branding, printing, signage, display and event solutions for businesses across the UAE. We help brands enhance their presence with impactful displays, premium printing and well-executed events.
            </p>
            <p className="mt-4 text-slate-600 dark:text-white/70 leading-relaxed">
              With a commitment to quality, creativity and timely delivery, we serve as a reliable one-stop solution for branding, printing, gifting and event requirements.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl"
          >
            <img
              src={src}
              alt="Printzee team"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-4 border-b-4 border-[#F59E0B] rounded-br-3xl" />
          </motion.div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="relative bg-[#0F172A] py-20 md:py-28 overflow-hidden">
        <div className="pz-blob bg-[#2563EB] w-[400px] h-[400px] top-[-160px] left-[-100px] opacity-40" />
        <div className="pz-blob bg-[#F59E0B] w-[320px] h-[320px] bottom-[-160px] right-[-80px] opacity-30" />
        <div className="relative pz-container grid grid-cols-1 lg:grid-cols-2 gap-7">
          {[
            { icon: Target, tag: "Our Mission", text: "Deliver high-quality products with creativity and reliability — for every brand, every brief, every deadline." },
            { icon: Eye, tag: "Our Vision", text: "Become one of the leading branding and printing companies in the UAE — recognised for craft, consistency and care." },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.tag}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="pz-glass-dark rounded-3xl p-9 text-white"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#F59E0B] text-[#0F172A] grid place-items-center">
                  <Icon className="w-7 h-7" strokeWidth={1.6} />
                </div>
                <div className="mt-7 text-[11px] uppercase tracking-[0.22em] text-[#F59E0B] font-semibold">{c.tag}</div>
                <p className="mt-3 font-display text-2xl sm:text-3xl leading-snug font-medium">&ldquo;{c.text}&rdquo;</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white dark:bg-[#0B1120] py-20 md:py-24">
        <div className="pz-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {STATS.map((s) => (
              <div key={s.l} data-testid={`about-stat-${s.l}`}>
                <div className="font-display text-5xl sm:text-6xl font-bold text-[#0F172A] dark:text-white">
                  <Counter to={s.v} />{s.suffix}
                </div>
                <div className="mt-3 text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-white/55">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#F8FAFC] dark:bg-white/[0.02] py-20 md:py-28 transition-colors">
        <div className="pz-container">
          <div className="max-w-2xl">
            <span className="pz-overline">Testimonials</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] dark:text-white">What Our Customers Say</h2>
            <div className="pz-gold-line mt-5" />
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {REVIEWS.map((r, i) => (
              <motion.div
                key={r.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`review-card-${i}`}
                className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.04] p-8 hover:-translate-y-1 hover:shadow-xl transition-all"
              >
                <div className="flex gap-1 text-[#F59E0B]">
                  {Array.from({ length: 5 }).map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="mt-5 font-display text-lg sm:text-xl text-[#0F172A] dark:text-white leading-snug">&ldquo;{r.quote}&rdquo;</p>
                <div className="mt-6 pt-5 border-t border-slate-100 dark:border-white/10">
                  <div className="font-display font-semibold text-[#0F172A] dark:text-white">{r.name}</div>
                  <div className="text-xs text-slate-500 dark:text-white/55 mt-1">{r.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
      <SiteFooter />
    </PageShell>
  );
}
